/**
 * FeatureFlagContext — BB-002
 *
 * Fetches feature flags from /api/config on mount and exposes them to the
 * component tree.  All flags default to `false` until the fetch completes,
 * so gated UI stays hidden during the initial load.
 *
 * Usage:
 *   const { sharedProjects } = useFeatureFlags();
 *   if (!sharedProjects) return null;  // hide sharing UI
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../lib/api';

const FeatureFlagContext = createContext({});

const DEFAULTS = {
  sharedProjects: false,
};

export function FeatureFlagProvider({ children }) {
  const [flags, setFlags] = useState(DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/config`);
        if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
        const data = await res.json();
        if (!cancelled && data.featureFlags) {
          setFlags({
            sharedProjects: !!data.featureFlags.shared_projects,
          });
        }
      } catch {
        // Flags stay at defaults (everything off) — fail closed.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}

export default FeatureFlagContext;
